import { authService, checkAWSCredentialsStatus } from "@/services"
import type { AccountStatus } from "@/types"
import { create } from "zustand"

const PAYPAL_SETUP_STORAGE_PREFIX = "cs_paypal_setup_complete:"

const getPaypalSetupStorageKey = (userId: string) => `${PAYPAL_SETUP_STORAGE_PREFIX}${userId}`

// PayPal has no dedicated backend endpoint yet, so a completed connection is
// persisted locally per-user. This is a one-time setup step — once done for
// a user it must not be re-requested just because the server-side
// has_credit_card flag hasn't caught up.
const readPaypalSetupComplete = (userId: string): boolean => {
  try {
    return localStorage.getItem(getPaypalSetupStorageKey(userId)) === "true"
  } catch {
    return false
  }
}

const writePaypalSetupComplete = (userId: string): void => {
  try {
    localStorage.setItem(getPaypalSetupStorageKey(userId), "true")
  } catch {
    // localStorage unavailable (e.g. private browsing) — setup will be
    // requested again next session, which is an acceptable fallback.
  }
}

const clearPaypalSetupComplete = (userId: string): void => {
  try {
    localStorage.removeItem(getPaypalSetupStorageKey(userId))
  } catch {
    // localStorage unavailable — nothing to clear.
  }
}

interface SetupStore {
  // Account Status State
  accountStatus: AccountStatus | null
  isLoading: boolean
  error: string | null
  // Guards autoCheckAwsCredentialsStatusOnce so it fires at most once per
  // login, not on every ProtectedRoute mount/navigation (see that action).
  hasCheckedAwsCredentialsThisSession: boolean

  // Actions
  fetchAccountStatus: () => Promise<void>
  setAccountStatus: (status: AccountStatus | null) => void
  autoCheckAwsCredentialsStatusOnce: () => Promise<void>
  completePaypalSetup: () => void
  disconnectPaypalSetup: () => void
  resetSetup: () => void
}

export const useSetupStore = create<SetupStore>((set, get) => ({
  // Initial state
  accountStatus: null,
  isLoading: false,
  error: null,
  hasCheckedAwsCredentialsThisSession: false,

  // Fetch account status from API
  fetchAccountStatus: async () => {
    set({ isLoading: true, error: null })
    try {
      const status = await authService.fetchAccountStatus()
      // Honor a previously completed PayPal setup even if the server
      // hasn't caught up (see comment above on why this is stored locally).
      const has_credit_card = status.has_credit_card || readPaypalSetupComplete(status.auth0_user_id)
      set({ accountStatus: { ...status, has_credit_card }, isLoading: false })
    } catch (error: any) {
      console.error("Failed to fetch account status:", error)
      set({
        error: error.message || "Failed to fetch account status",
        isLoading: false,
        accountStatus: null
      })
    }
  },

  // Manually set account status (useful for optimistic updates). Applies the
  // same locally-persisted PayPal-setup override as fetchAccountStatus, so a
  // caller that fetched status itself (e.g. AwsConfigurationSettings, which
  // avoids the store's own fetchAccountStatus to keep its isLoading flag
  // local) doesn't clobber a completed PayPal setup with the server's
  // still-false has_credit_card.
  setAccountStatus: (status) => {
    if (!status) {
      set({ accountStatus: null })
      return
    }
    const has_credit_card = status.has_credit_card || readPaypalSetupComplete(status.auth0_user_id)
    set({ accountStatus: { ...status, has_credit_card } })
  },

  // Fires a live AWS check against the currently stored credentials at most
  // once per login, so a key that was revoked/suspended after it was
  // originally validated is caught as soon as the app loads — not only when
  // the user happens to open Settings. Safe to call from every
  // ProtectedRoute mount (i.e. every navigation): the guard below makes every
  // call after the first a no-op until resetSetup() (logout) clears it.
  // Deliberately not awaited by callers — this runs in the background and
  // never blocks rendering; a transient failure here is silently swallowed
  // since the user can always recheck manually from Settings.
  autoCheckAwsCredentialsStatusOnce: async () => {
    if (get().hasCheckedAwsCredentialsThisSession) return
    set({ hasCheckedAwsCredentialsThisSession: true })

    const { accountStatus } = get()
    if (accountStatus?.user_type !== "superadmin" || !accountStatus.has_aws_credentials) return

    try {
      const result = await checkAWSCredentialsStatus()
      const current = get().accountStatus
      if (current) {
        set({ accountStatus: { ...current, aws_validation_status: result.status } })
      }
    } catch (error) {
      console.error("Background AWS credentials check failed:", error)
    }
  },

  // Marks the one-time PayPal setup complete for the current user: persists
  // it to localStorage (source of truth until a real backend endpoint
  // exists) and updates state so the rest of the app unblocks immediately.
  completePaypalSetup: () => {
    const { accountStatus } = get()
    if (!accountStatus) return
    writePaypalSetupComplete(accountStatus.auth0_user_id)
    set({ accountStatus: { ...accountStatus, has_credit_card: true } })
  },

  // Reverses completePaypalSetup: clears the localStorage flag and updates
  // state. For a superadmin this re-triggers the payment setup gate in
  // ProtectedRoute, so callers should confirm with the user first.
  disconnectPaypalSetup: () => {
    const { accountStatus } = get()
    if (!accountStatus) return
    clearPaypalSetupComplete(accountStatus.auth0_user_id)
    set({ accountStatus: { ...accountStatus, has_credit_card: false } })
  },

  // Reset all setup states (useful for logout or testing)
  resetSetup: () =>
    set({
      accountStatus: null,
      isLoading: false,
      error: null,
      hasCheckedAwsCredentialsThisSession: false,
    }),
}))

// Selector functions for computed properties
export const selectPaymentSetupComplete = (state: SetupStore) =>
  state.accountStatus?.has_credit_card ?? false

export const selectAwsSetupComplete = (state: SetupStore) => {
  // Consider AWS setup complete if credentials exist and validation hasn't failed
  // This allows users to access the dashboard while validation is pending or in progress
  const hasCredentials = state.accountStatus?.has_aws_credentials === true
  const validationStatus = state.accountStatus?.aws_validation_status
  const isFailed = validationStatus === "failed"

  return hasCredentials && !isFailed
}
